import { useLocalization } from "@fluent/react";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import {
    Box,
    CircularProgress,
    IconButton,
    Portal,
    Tooltip,
    useTheme,
} from "@mui/material";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { useAppDispatch } from "../hooks";
import { BugReportDialog } from "../../features/support/components/BugReportDialog";
import { captureViewport } from "../../features/support/helpers/bugAttachmentHelpers";
import { clearSupportError } from "../../features/support/supportIntegrationSlice";


export const BugReportButton = () => {
    const { l10n } = useLocalization();

    const theme = useTheme();

    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);

    const [capturing, setCapturing] = useState(false);

    const [initialScreenshot, setInitialScreenshot] = useState<Blob | null>(null);

    const [initialCaptureError, setInitialCaptureError] = useState(false);

    const triggerAnchorRef = useRef<HTMLButtonElement | null>(null);

    const [triggerPosition, setTriggerPosition] = useState<{
        top: number;
        left: number;
    } | null>(null);


    const handleOpen = useCallback(async () => {
        dispatch(clearSupportError());

        setCapturing(true);

        try {
            setInitialScreenshot(await captureViewport());
            setInitialCaptureError(false);
        } catch {
            setInitialScreenshot(null);
            setInitialCaptureError(true);
        } finally {
            setCapturing(false);
        }

        setOpen(true);
    }, [dispatch]);


    const handleClose = useCallback(() => setOpen(false), []);


    const updateTriggerPosition = useCallback(() => {
        const rect = triggerAnchorRef.current?.getBoundingClientRect();

        if (!rect) {
            return;
        }

        setTriggerPosition((previousPosition) => {
            const nextPosition = {
                top: rect.top,
                left: rect.left,
            };

            if (
                previousPosition
                && Math.abs(previousPosition.top - nextPosition.top) < 0.5
                && Math.abs(previousPosition.left - nextPosition.left) < 0.5
            ) {
                return previousPosition;
            }

            return nextPosition;
        });
    }, []);


    useEffect(() => {
        updateTriggerPosition();

        window.addEventListener("resize", updateTriggerPosition);
        window.addEventListener("scroll", updateTriggerPosition, true);

        return () => {
            window.removeEventListener("resize", updateTriggerPosition);
            window.removeEventListener("scroll", updateTriggerPosition, true);
        };
    }, [updateTriggerPosition]);


    useEffect(() => {
        updateTriggerPosition();
    });


    const triggerButton = (
        <Tooltip
            title={l10n.getString(
                "bug-report-action",
                null,
                "Report a bug"
            )}
        >
            <span data-bug-report-exclude>
                <IconButton
                    color="inherit"
                    size="small"
                    onClick={handleOpen}
                    disabled={capturing || open}
                    sx={{
                        color: "common.white",
                        bgcolor: "transparent",
                        "&:hover": {
                            bgcolor: "rgba(255, 255, 255, 0.12)",
                        },
                        "&.Mui-disabled": {
                            bgcolor: "transparent",
                            color: "rgba(255, 255, 255, 0.45)",
                        },
                    }}
                    aria-label={l10n.getString(
                        "bug-report-action",
                        null,
                        "Report a bug"
                    )}
                >
                    {capturing
                        ? (
                            <CircularProgress
                                size={20}
                                color="inherit"
                            />
                        )
                        : (
                            <BugReportOutlinedIcon
                                fontSize="small"
                            />
                        )}
                </IconButton>
            </span>
        </Tooltip>
    );


    return (
        <>
            <IconButton
                ref={triggerAnchorRef}
                size="small"
                sx={{ visibility: "hidden", pointerEvents: "none" }}
                aria-hidden
                tabIndex={-1}
            >
                <BugReportOutlinedIcon fontSize="small" />
            </IconButton>

            <Portal>
                <Box
                    sx={{
                        position: "fixed",
                        top: triggerPosition?.top ?? -1000,
                        left: triggerPosition?.left ?? -1000,
                        zIndex: theme.zIndex.modal + 2,
                    }}
                    data-bug-report-exclude
                >
                    {triggerButton}
                </Box>
            </Portal>


            <BugReportDialog
                open={open}
                onClose={handleClose}
                initialScreenshot={initialScreenshot}
                initialCaptureError={initialCaptureError}
            />
        </>
    );
};